// components/common/PeriodFilterBar.jsx
// Shared "Daily / Weekly / Monthly / Custom / All" period bar + agent + sort +
// search, matching the MIS Report's filter bar. Purely a controlled UI piece —
// callers own all state and do the actual filtering (see utils/periodFilter.js).
import React, { useMemo } from "react";
import {
  ChevronLeft, ChevronRight, Users, Search, X, SortAsc, SortDesc,
} from "lucide-react";
import { PERIODS, getWedMonRange, addDays } from "../../utils/periodFilter";
import "../../css/common/periodFilterBar.css";

export default function PeriodFilterBar({
  period, onPeriodChange,
  weekAnchor, onWeekAnchorChange,
  customFrom, onCustomFromChange, customTo, onCustomToChange,
  showAgent = false, agentValue = "", onAgentChange, agentOptions = [], agentPlaceholder = "All Agents",
  sortBy, onSortByChange, sortDir, onSortDirChange, sortOptions = [],
  search, onSearchChange, searchPlaceholder = "Search…",
  children,
}) {
  const weeklyRange = useMemo(
    () => getWedMonRange(weekAnchor || new Date()),
    [weekAnchor]
  );
  const canGoNextWeek = weeklyRange.start < getWedMonRange(new Date()).start;

  const handlePeriodChange = (value) => {
    onPeriodChange(value);
    if (value === "weekly" && onWeekAnchorChange) onWeekAnchorChange(new Date());
  };

  return (
    <div className="pfb-bar">
      {/* Period tabs */}
      <div className="pfb-tabs">
        {PERIODS.map((p) => (
          <button key={p.value}
            type="button"
            className={`pfb-tab ${period === p.value ? "active" : ""}`}
            onClick={() => handlePeriodChange(p.value)}>
            {p.label}
          </button>
        ))}
      </div>

      {/* Weekly Wed→Mon range, with prev/next week navigation */}
      {period === "weekly" && onWeekAnchorChange && (
        <div className="pfb-week-nav">
          <button type="button" className="pfb-week-nav-btn"
            onClick={() => onWeekAnchorChange(addDays(weekAnchor || new Date(), -7))}
            title="Previous week">
            <ChevronLeft size={14}/>
          </button>
          <span className="pfb-week-nav-label">
            {weeklyRange.start.toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}
            {" (Wed) → "}
            {weeklyRange.end.toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}
            {" (Mon)"}
          </span>
          <button type="button" className="pfb-week-nav-btn"
            onClick={() => onWeekAnchorChange(addDays(weekAnchor || new Date(), 7))}
            disabled={!canGoNextWeek} title="Next week">
            <ChevronRight size={14}/>
          </button>
        </div>
      )}

      {/* Custom date range */}
      {period === "custom" && (
        <div className="pfb-custom-range">
          <input type="date" value={customFrom || ""} onChange={(e) => onCustomFromChange(e.target.value)}/>
          <span>→</span>
          <input type="date" value={customTo || ""} onChange={(e) => onCustomToChange(e.target.value)}/>
          {(!customFrom || !customTo) && <span className="pfb-custom-range-hint">Pick both dates</span>}
        </div>
      )}

      {children}

      {/* Agent filter (admin/manager only) */}
      {showAgent && (
        <div className="pfb-filter-group">
          <Users size={13}/>
          <select value={agentValue} onChange={(e) => onAgentChange(e.target.value)}>
            <option value="">{agentPlaceholder}</option>
            {agentOptions.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      )}

      {/* Sort by */}
      {sortOptions.length > 0 && (
        <div className="pfb-filter-group">
          {sortDir === "asc" ? <SortAsc size={13}/> : <SortDesc size={13}/>}
          <select value={sortBy} onChange={(e) => onSortByChange(e.target.value)}>
            {sortOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <button type="button" className="pfb-sort-dir-btn"
            onClick={() => onSortDirChange(sortDir === "asc" ? "desc" : "asc")}
            title="Toggle sort direction">
            {sortDir === "asc" ? "↑" : "↓"}
          </button>
        </div>
      )}

      {/* Search */}
      {onSearchChange && (
        <div className="pfb-search-box">
          <Search size={13} className="pfb-search-icon"/>
          <input
            type="text"
            className="pfb-search-input"
            placeholder={searchPlaceholder}
            value={search || ""}
            onChange={(e) => onSearchChange(e.target.value)}
          />
          {search && (
            <button type="button" className="pfb-search-clear" onClick={() => onSearchChange("")}>
              <X size={11}/>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
